import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Text, Flex, HStack, VStack, IconButton,
  Grid, GridItem, useBreakpointValue,
  Popover, PopoverTrigger, PopoverContent, PopoverBody, PopoverArrow,
  Heading, Tooltip,
} from "@chakra-ui/react";
import { FiChevronLeft, FiChevronRight, FiMapPin, FiClock, FiCalendar } from "react-icons/fi";
import moment from "moment";

// Palette for course color-coding
const COURSE_COLORS = [
  { bg: "#EBF4FF", border: "#3182CE", text: "#1A365D", dot: "#3182CE" }, // blue
  { bg: "#F0FFF4", border: "#38A169", text: "#1C4532", dot: "#38A169" }, // green
  { bg: "#FAF5FF", border: "#805AD5", text: "#322659", dot: "#805AD5" }, // purple
  { bg: "#FFFAF0", border: "#DD6B20", text: "#652B19", dot: "#DD6B20" }, // orange
  { bg: "#E6FFFA", border: "#319795", text: "#1D4044", dot: "#319795" }, // teal
  { bg: "#FFF5F5", border: "#E53E3E", text: "#63171B", dot: "#E53E3E" }, // red
  { bg: "#FEFCBF", border: "#D69E2E", text: "#744210", dot: "#D69E2E" }, // yellow
  { bg: "#FED7E2", border: "#D53F8C", text: "#521B41", dot: "#D53F8C" }, // pink
];

const DAYS_OF_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function formatTime(timeStr) {
  if (!timeStr) return "";
  // Handle HHMM format (e.g., "0900", "1700")
  if (/^\d{4}$/.test(timeStr)) {
    return `${timeStr.slice(0, 2)}:${timeStr.slice(2)}`;
  }
  // Handle HH:mm:ss or HH:mm
  if (/^\d{2}:\d{2}/.test(timeStr)) {
    return timeStr.slice(0, 5);
  }
  return timeStr;
}

export function CourseCalendarView({ certifications = [], modules = {}, standaloneCourses = [] }) {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const navigate = useNavigate();

  // --- Build unified event list with color assignments ---
  const { events, legend } = useMemo(() => {
    const allEvents = [];
    const colorMap = {};
    const legend = [];
    let colorIndex = 0;

    const assignColor = (key, label, type) => {
      if (!colorMap[key]) {
        colorMap[key] = { ...COURSE_COLORS[colorIndex % COURSE_COLORS.length], label, type };
        legend.push({ key, label, type, color: colorMap[key] });
        colorIndex++;
      }
      return colorMap[key];
    };

    // Certification modules
    certifications.forEach(cert => {
      const certLabel = cert.full_certification?.name || cert.run_name || "Certification";
      const certMods = modules[cert.id] || [];
      certMods.forEach(module => {
        const modLabel = module.course_type?.name || module.course_type_code || "Module";
        const colorKey = `cert-${cert.id}-mod-${module.id}`;
        const color = assignColor(colorKey, modLabel, "module");

        (module.course_dates || []).forEach(cd => {
          if (!cd.date) return;
          allEvents.push({
            date: moment(cd.date).format("YYYY-MM-DD"),
            title: modLabel,
            shortTitle: (module.course_type?.code || modLabel).slice(0, 12),
            startTime: cd.start_time,
            endTime: cd.end_time,
            venue: cd.venue,
            room: cd.room,
            certificationName: certLabel,
            moduleId: module.id,
            courseRunId: module.id,
            colorKey,
            color,
          });
        });
      });
    });

    // Standalone courses
    standaloneCourses.forEach(course => {
      const courseLabel = course.course_type?.name || "Course";
      const colorKey = `standalone-${course.id}`;
      const color = assignColor(colorKey, courseLabel, "course");

      (course.course_dates || []).forEach(cd => {
        if (!cd.date) return;
        allEvents.push({
          date: moment(cd.date).format("YYYY-MM-DD"),
          title: courseLabel,
          shortTitle: (course.course_type?.code || courseLabel).slice(0, 12),
          startTime: cd.start_time,
          endTime: cd.end_time,
          venue: cd.venue,
          room: cd.room,
          certificationName: null,
          courseRunId: course.id,
          colorKey,
          color,
        });
      });
    });

    // Sort events by time within each day
    allEvents.sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return (a.startTime || "").localeCompare(b.startTime || "");
    });

    return { events: allEvents, legend };
  }, [certifications, modules, standaloneCourses]);

  // --- Determine date range for month navigation ---
  const { minMonth, maxMonth } = useMemo(() => {
    if (events.length === 0) {
      return {
        minMonth: moment().startOf("month"),
        maxMonth: moment().endOf("month"),
      };
    }
    const sorted = [...events].sort((a, b) => a.date.localeCompare(b.date));
    return {
      minMonth: moment(sorted[0].date).startOf("month"),
      maxMonth: moment(sorted[sorted.length - 1].date).startOf("month"),
    };
  }, [events]);

  const [currentMonth, setCurrentMonth] = useState(() => {
    // Start at the first month that has events, or current month
    const today = moment().startOf("month");
    if (events.length === 0) return today;
    const firstEventMonth = moment(events[0].date).startOf("month");
    return firstEventMonth.isBefore(today) ? today : firstEventMonth;
  });

  const canGoPrev = currentMonth.isAfter(minMonth);
  const canGoNext = currentMonth.isBefore(maxMonth);

  const goToPrev = () => setCurrentMonth(m => moment(m).subtract(1, "month"));
  const goToNext = () => setCurrentMonth(m => moment(m).add(1, "month"));

  // --- Build calendar grid for current month ---
  const calendarDays = useMemo(() => {
    const start = moment(currentMonth).startOf("month");
    const end = moment(currentMonth).endOf("month");

    // Find Monday of the week containing the 1st
    const gridStart = moment(start).startOf("isoWeek");
    // Find Sunday of the week containing the last day
    const gridEnd = moment(end).endOf("isoWeek");

    const days = [];
    let day = moment(gridStart);
    while (day.isSameOrBefore(gridEnd)) {
      days.push(moment(day));
      day.add(1, "day");
    }
    return days;
  }, [currentMonth]);

  // --- Index events by date ---
  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach(evt => {
      if (!map[evt.date]) map[evt.date] = [];
      map[evt.date].push(evt);
    });
    return map;
  }, [events]);

  const today = moment().format("YYYY-MM-DD");
  const monthStr = currentMonth.format("MMMM YYYY");

  if (events.length === 0) {
    return (
      <Box textAlign="center" py={10} color="gray.500">
        <FiCalendar size={32} style={{ margin: "0 auto 12px" }} />
        <Text>No sessions scheduled yet.</Text>
      </Box>
    );
  }

  return (
    <Box>
      {/* Month navigation header */}
      <Flex align="center" justify="space-between" mb={4}>
        <IconButton
          icon={<FiChevronLeft />}
          aria-label="Previous month"
          variant="ghost"
          onClick={goToPrev}
          isDisabled={!canGoPrev}
          size="sm"
        />
        <Heading size="sm" color="gray.700" fontWeight="700" letterSpacing="tight">
          {monthStr}
        </Heading>
        <IconButton
          icon={<FiChevronRight />}
          aria-label="Next month"
          variant="ghost"
          onClick={goToNext}
          isDisabled={!canGoNext}
          size="sm"
        />
      </Flex>

      {/* Day-of-week headers */}
      <Grid templateColumns="repeat(7, 1fr)" gap={0} mb={1}>
        {DAYS_OF_WEEK.map(d => (
          <GridItem key={d}>
            <Text
              textAlign="center"
              fontSize={{ base: "2xs", md: "xs" }}
              fontWeight="600"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="0.05em"
              py={1}
            >
              {isMobile ? d.slice(0, 1) : d}
            </Text>
          </GridItem>
        ))}
      </Grid>

      {/* Calendar grid */}
      <Grid
        templateColumns="repeat(7, 1fr)"
        gap={{ base: "2px", md: "3px" }}
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        overflow="hidden"
        bg="gray.200"
      >
        {calendarDays.map((day, idx) => {
          const dateKey = day.format("YYYY-MM-DD");
          const isCurrentMonth = day.isSame(currentMonth, "month");
          const isToday = dateKey === today;
          const dayEvents = eventsByDate[dateKey] || [];
          const hasEvents = dayEvents.length > 0;

          return (
            <GridItem
              key={idx}
              bg={isCurrentMonth ? (hasEvents ? "white" : "gray.50") : "gray.100"}
              minH={{ base: "52px", md: "80px" }}
              p={{ base: "2px", md: "4px" }}
              opacity={isCurrentMonth ? 1 : 0.5}
              position="relative"
            >
              {/* Day number */}
              <Flex justify="flex-end" mb={{ base: "1px", md: "3px" }}>
                <Box
                  w={{ base: "18px", md: "22px" }}
                  h={{ base: "18px", md: "22px" }}
                  borderRadius="full"
                  bg={isToday ? "blue.500" : "transparent"}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text
                    fontSize={{ base: "2xs", md: "xs" }}
                    fontWeight={isToday ? "700" : "500"}
                    color={isToday ? "white" : isCurrentMonth ? "gray.700" : "gray.400"}
                    lineHeight="1"
                  >
                    {day.date()}
                  </Text>
                </Box>
              </Flex>

              {/* Events */}
              <VStack spacing="1px" align="stretch">
                {isMobile ? (
                  // Mobile: dots only with popover
                  dayEvents.length > 0 && (
                    <Popover placement="auto" isLazy>
                      <PopoverTrigger>
                        <Flex
                          gap="2px"
                          flexWrap="wrap"
                          justify="center"
                          cursor="pointer"
                          _hover={{ opacity: 0.8 }}
                        >
                          {dayEvents.slice(0, 4).map((evt, i) => (
                            <Box
                              key={i}
                              w="6px"
                              h="6px"
                              borderRadius="full"
                              bg={evt.color.dot}
                              flexShrink={0}
                            />
                          ))}
                          {dayEvents.length > 4 && (
                            <Text fontSize="2xs" color="gray.500" lineHeight="1">
                              +{dayEvents.length - 4}
                            </Text>
                          )}
                        </Flex>
                      </PopoverTrigger>
                      <PopoverContent zIndex={1400} maxW="260px" shadow="lg">
                        <PopoverArrow />
                        <PopoverBody p={3}>
                          <Text fontWeight="700" fontSize="sm" mb={2} color="gray.700">
                            {day.format("ddd, DD MMM YYYY")}
                          </Text>
                          <VStack align="stretch" spacing={2}>
                            {dayEvents.map((evt, i) => (
                              <EventPopoverCard
                                key={i}
                                evt={evt}
                                onClick={() => evt.courseRunId && navigate(`/courses/${evt.courseRunId}`)}
                                clickable={!!evt.courseRunId}
                              />
                            ))}
                          </VStack>
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>
                  )
                ) : (
                  // Desktop: pill events with hover tooltip and click → navigate to course
                  <>
                    {dayEvents.slice(0, 3).map((evt, i) => (
                      <Tooltip
                        key={i}
                        label={
                          <Box p={0} bg="transparent" maxW="280px">
                            <EventPopoverCard evt={evt} />
                          </Box>
                        }
                        placement="top"
                        hasArrow
                        openDelay={300}
                        closeDelay={100}
                        bg="white"
                        color="gray.800"
                        borderWidth="1px"
                        borderColor="gray.200"
                        shadow="lg"
                        borderRadius="md"
                        py={0}
                        px={0}
                      >
                        <Box
                          px="4px"
                          py="1px"
                          borderRadius="3px"
                          bg={evt.color.bg}
                          borderLeft="3px solid"
                          borderLeftColor={evt.color.border}
                          cursor="pointer"
                          _hover={{ opacity: 0.85, transform: "scale(1.01)" }}
                          transition="all 0.1s"
                          overflow="hidden"
                          onClick={() => evt.courseRunId && navigate(`/courses/${evt.courseRunId}`)}
                          role={evt.courseRunId ? "button" : undefined}
                          tabIndex={evt.courseRunId ? 0 : undefined}
                          onKeyDown={(e) => {
                            if (evt.courseRunId && (e.key === "Enter" || e.key === " ")) {
                              e.preventDefault();
                              navigate(`/courses/${evt.courseRunId}`);
                            }
                          }}
                        >
                          <Text
                            fontSize="2xs"
                            fontWeight="600"
                            color={evt.color.text}
                            noOfLines={1}
                            lineHeight="1.4"
                          >
                            {evt.startTime ? `${formatTime(evt.startTime)} ` : ""}
                            {evt.shortTitle}
                          </Text>
                        </Box>
                      </Tooltip>
                    ))}
                    {dayEvents.length > 3 && (
                      <Popover placement="auto" isLazy>
                        <PopoverTrigger>
                          <Text
                            fontSize="2xs"
                            color="gray.500"
                            fontWeight="600"
                            pl="4px"
                            cursor="pointer"
                            _hover={{ color: "gray.700" }}
                          >
                            +{dayEvents.length - 3} more
                          </Text>
                        </PopoverTrigger>
                        <PopoverContent zIndex={1400} maxW="280px" shadow="lg">
                          <PopoverArrow />
                          <PopoverBody p={3}>
                            <Text fontWeight="700" fontSize="sm" mb={2} color="gray.700">
                              {day.format("ddd, DD MMM YYYY")}
                            </Text>
                            <VStack align="stretch" spacing={2}>
                              {dayEvents.map((evt, i) => (
                                <EventPopoverCard
                                  key={i}
                                  evt={evt}
                                  onClick={() => evt.courseRunId && navigate(`/courses/${evt.courseRunId}`)}
                                  clickable={!!evt.courseRunId}
                                />
                              ))}
                            </VStack>
                          </PopoverBody>
                        </PopoverContent>
                      </Popover>
                    )}
                  </>
                )}
              </VStack>
            </GridItem>
          );
        })}
      </Grid>

      {/* Legend */}
      {legend.length > 0 && (
        <Box mt={5} pt={4} borderTop="1px solid" borderColor="gray.100">
          <Text fontSize="xs" fontWeight="600" color="gray.500" mb={3} textTransform="uppercase" letterSpacing="0.05em">
            Course Legend
          </Text>
          <Flex gap={3} flexWrap="wrap">
            {legend.map(item => (
              <HStack
                key={item.key}
                spacing={2}
                px={3}
                py={1.5}
                borderRadius="full"
                bg={item.color.bg}
                border="1px solid"
                borderColor={item.color.border}
              >
                <Box w="8px" h="8px" borderRadius="full" bg={item.color.dot} flexShrink={0} />
                <Text fontSize="xs" fontWeight="600" color={item.color.text} noOfLines={1} maxW="180px">
                  {item.label}
                </Text>
              </HStack>
            ))}
          </Flex>
        </Box>
      )}

      {/* Month summary strip */}
      <MonthSummary
        currentMonth={currentMonth}
        eventsByDate={eventsByDate}
        legend={legend}
      />
    </Box>
  );
}

function EventPopoverCard({ evt, onClick, clickable }) {
  return (
    <Box
      p={2.5}
      borderRadius="md"
      bg={evt.color.bg}
      borderLeft="4px solid"
      borderLeftColor={evt.color.border}
      {...(clickable && {
        cursor: "pointer",
        _hover: { opacity: 0.9 },
        onClick,
        role: "button",
        tabIndex: 0,
        onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(); } },
      })}
    >
      <Text fontWeight="700" fontSize="sm" color={evt.color.text} mb={1} noOfLines={2}>
        {evt.title}
      </Text>
      {evt.certificationName && (
        <Text fontSize="xs" color="gray.500" mb={1.5} fontStyle="italic">
          {evt.certificationName}
        </Text>
      )}
      {(evt.startTime || evt.endTime) && (
        <HStack spacing={1} color="gray.600" mb={1}>
          <FiClock size={11} />
          <Text fontSize="xs">
            {formatTime(evt.startTime)}{evt.endTime ? ` – ${formatTime(evt.endTime)}` : ""}
          </Text>
        </HStack>
      )}
      {evt.venue && evt.venue !== "TBC" && (
        <HStack spacing={1} color="gray.600" align="flex-start">
          <Box mt="1px" flexShrink={0}><FiMapPin size={11} /></Box>
          <Text fontSize="xs" noOfLines={2}>
            {evt.venue}{evt.room ? ` · ${evt.room}` : ""}
          </Text>
        </HStack>
      )}
    </Box>
  );
}

function MonthSummary({ currentMonth, eventsByDate, legend }) {
  const monthEvents = useMemo(() => {
    const monthStr = currentMonth.format("YYYY-MM");
    const evts = [];
    Object.entries(eventsByDate).forEach(([date, dayEvts]) => {
      if (date.startsWith(monthStr)) evts.push(...dayEvts);
    });
    return evts;
  }, [currentMonth, eventsByDate]);

  if (monthEvents.length === 0) return null;

  // Count per color key
  const counts = {};
  monthEvents.forEach(evt => {
    counts[evt.colorKey] = (counts[evt.colorKey] || 0) + 1;
  });

  // Count unique session days
  const uniqueDays = new Set(monthEvents.map(e => e.date)).size;

  return (
    <Box mt={4} p={4} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200">
      <HStack spacing={4} flexWrap="wrap" align="flex-start">
        <VStack align="flex-start" spacing={0} minW="80px">
          <Text fontSize="2xl" fontWeight="700" color="gray.800" lineHeight="1">
            {uniqueDays}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {uniqueDays === 1 ? "class day" : "class days"}
          </Text>
        </VStack>
        <Box w="1px" h="40px" bg="gray.300" alignSelf="center" />
        <VStack align="flex-start" spacing={0} minW="80px">
          <Text fontSize="2xl" fontWeight="700" color="gray.800" lineHeight="1">
            {monthEvents.length}
          </Text>
          <Text fontSize="xs" color="gray.500">
            {monthEvents.length === 1 ? "session" : "sessions"}
          </Text>
        </VStack>
        <Box w="1px" h="40px" bg="gray.300" alignSelf="center" display={{ base: "none", md: "block" }} />
        <Flex gap={3} flexWrap="wrap" flex="1">
          {legend.filter(l => counts[l.key]).map(l => (
            <HStack key={l.key} spacing={1.5}>
              <Box w="6px" h="6px" borderRadius="full" bg={l.color.dot} />
              <Text fontSize="xs" color="gray.600">
                <Text as="span" fontWeight="700">{counts[l.key]}</Text>
                {" "}{l.label.length > 24 ? l.label.slice(0, 24) + "…" : l.label}
              </Text>
            </HStack>
          ))}
        </Flex>
      </HStack>
    </Box>
  );
}
